import { useTranslate } from '@tolgee/react';
import { Button, TableColumnsType, TableProps, Tooltip } from 'antd';
import ScrollTable from 'components/ScrollTable';
import { ReactNode, useMemo } from 'react';
import { formatNumber } from 'utils/numberFormat';
import { durationDisplay } from '../ImplementData/utils';
import { ExclamationCircleFilled } from '@ant-design/icons';
import { What } from 'views/Aide/Scenario/PolicyTable';
import styled from '@emotion/styled';
import { isStoreAliasFirst, useStoreNameConfig } from 'hooks/business/store/useStoreName';

const getSeconds = (num: number, unit: number) => {
  return typeof num === 'number'
    ? unit === 5
      ? num * 60 * 60
      : unit === 4
      ? num * 24 * 60 * 60
      : unit === 3
      ? num * 60
      : undefined
    : undefined;
};

type RT = Dashboard.PlatformData.StorePerfData;

function ShopeePerformanceTable(
  props: TableProps<RT> & {
    onCheckDetail: (record: RT) => void;
    onCheckTrend: (record: RT) => void;
  },
) {
  const { dataSource: dataSourceProp, onCheckDetail, onCheckTrend, ...otherProps } = props;

  const dataSource = useMemo(() => {
    return dataSourceProp?.map(({ data, ...others }) => {
      try {
        const jsonData = (JSON.parse(data) as any[]).reduce<any>((res, cur) => {
          res[cur.metric_name] = [cur.current_period, cur.unit];
          return res;
        }, {});
        return {
          ...others,
          ...jsonData,
        };
      } catch (_) {
        return others;
      }
    });
  }, [dataSourceProp]);

  const { t } = useTranslate();

  const { config } = useStoreNameConfig();

  const percentRender = (percent: number) => formatNumber(percent / 100, '0.0%');

  const invalidRender = (node: ReactNode, invalid: boolean) =>
    invalid ? (
      <Invalid>
        {node}
        <ExclamationCircleFilled />
      </Invalid>
    ) : (
      node
    );

  const columns: TableColumnsType<any> = [
    {
      key: 'region',
      dataIndex: 'region',
      title: t('站点'),
      width: 84,
    },
    {
      key: 'storeName',
      dataIndex: 'storeName',
      title: t('店铺名称'),
      width: 190,
      render: (_, { storeAlias, storeName, region }) =>
        isStoreAliasFirst(config, 'SHOPEE', region) ? storeAlias || storeName : storeName,
    },
    {
      key: 'shop_rating',
      dataIndex: 'shop_rating',
      title: (
        <div>
          {t('店铺评价')}
          <Tooltip title={t('店铺中所有商品评价的平均值')}>
            <What />
          </Tooltip>
        </div>
      ),
      width: 140,
      render: (shopRating) => formatNumber(shopRating?.[0], '0.00'),
      sorter: true,
      showSorterTooltip: false,
    },
    {
      key: 'csat_rate',
      dataIndex: 'csat_rate',
      title: (
        <div>
          {t('聊天满意度')}
          <Tooltip
            title={t('会话收到买家“好评”的数量占总评价数（“好评”、“一般”和“差评”）的百分比')}
          >
            <What />
          </Tooltip>
        </div>
      ),
      width: 160,
      render: ([num] = [], record) => (
        <div className="flex items-center">
          {invalidRender(percentRender(num), num < 70)}
          <Button className="ml-2" type="link" onClick={() => onCheckDetail(record)}>
            {t('todo.明细')}
          </Button>
        </div>
      ),
      sorter: true,
      showSorterTooltip: false,
    },
    {
      key: 'response_rate',
      dataIndex: 'response_rate',
      title: (
        <div>
          {t('回复率')}
          <Tooltip
            title={t(
              '卖家在收到新聊天消息和报价后12小时内回复的比例（占总数的百分比），自动回复不计入回复率的计算。',
            )}
          >
            <What />
          </Tooltip>
        </div>
      ),
      width: 112,
      render: ([num] = []) => invalidRender(percentRender(num), num < 90),
      sorter: true,
      showSorterTooltip: false,
    },
    {
      key: 'response_time',
      dataIndex: 'response_time',
      title: (
        <div>
          {t('首次响应时长')}
          <Tooltip title={t('首次响应时长tips')}>
            <What />
          </Tooltip>
        </div>
      ),
      width: 160,
      render: ([num, unit] = []) => durationDisplay(getSeconds(num, unit)),
      sorter: true,
      showSorterTooltip: false,
    },
    {
      key: 'average_chat_response_time',
      dataIndex: 'average_chat_response_time',
      title: (
        <div>
          {t('平均响应时长')}
          <Tooltip title={t('平均响应时长tips3')}>
            <What />
          </Tooltip>
        </div>
      ),
      width: 160,
      render: ([num, unit] = []) => {
        const time = getSeconds(num, unit);
        return invalidRender(durationDisplay(time), time !== undefined && time > 60 * 60);
      },
      sorter: true,
      showSorterTooltip: false,
    },
    {
      key: 'non_fulfillment_rate',
      dataIndex: 'non_fulfillment_rate',
      title: (
        <div>
          {t('未完成率')}
          <Tooltip title={t('由于卖家原因导致取消或退货退款的订单，占总订单的百分比。')}>
            <What />
          </Tooltip>
        </div>
      ),
      width: 150,
      render: ([num] = []) => invalidRender(percentRender(num), num > 10),
      sorter: true,
      showSorterTooltip: false,
    },
    {
      key: 'cancellation_rate',
      dataIndex: 'cancellation_rate',
      title: (
        <div>
          {t('取消率')}
          <Tooltip title={t('卖家取消的订单占总订单的百分比，不包括买家发起取消的订单')}>
            <What />
          </Tooltip>
        </div>
      ),
      width: 150,
      render: ([num] = []) => invalidRender(percentRender(num), num > 7.5),
      sorter: true,
      showSorterTooltip: false,
    },
    {
      key: 'return_refund_rate',
      dataIndex: 'return_refund_rate',
      title: (
        <div>
          {t('退换货率')}
          <Tooltip title={t('买家请求退货退款的订单占总订单的百分比')}>
            <What />
          </Tooltip>
        </div>
      ),
      width: 150,
      render: ([num] = []) => invalidRender(percentRender(num), num > 7.5),
      sorter: true,
      showSorterTooltip: false,
    },
    {
      key: 'late_shipment_rate',
      dataIndex: 'late_shipment_rate',
      title: (
        <div>
          {t('延迟发货率')}
          <Tooltip title={t('延迟发货的订单数量，占总订单数量的百分比。')}>
            <What />
          </Tooltip>
        </div>
      ),
      width: 150,
      render: ([num] = []) => invalidRender(percentRender(num), num > 10),
      sorter: true,
      showSorterTooltip: false,
    },
    {
      key: 'operate',
      title: t('操作'),
      width: 116,
      fixed: 'right',
      render: (_, record) => (
        <Button type="link" onClick={() => onCheckTrend(record)}>
          {t('查看趋势')}
        </Button>
      ),
    },
  ];

  return <ScrollTable dataSource={dataSource} columns={columns} {...otherProps} />;
}

export const Invalid = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
  color: var(--ant-error-color);
`;

export default ShopeePerformanceTable;
